import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Wallet, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '@/services/apiService';
import { playClickSound } from '@/utils/soundUtils';
import { shouldOpenInExternalTab } from '@/features/finance/utils/paymentUrlStrategy';
import { StatusPill } from '@/components/ui';
import { typeRole, fsClass } from '@/styles/typography';

type PendingStatus = 'pending';

interface PendingTransaction {
  id: string;
  paymentUrl: string;
  transactionId?: string;
  platformName: string;
  platformSlug: string;
  amount: number;
  status: PendingStatus;
  createdAt: string;
  provider?: string | null;
  isWalletDeposit: boolean;
}

interface HistoryTransaction {
  id?: number | string;
  transactionId?: string;
  type?: string;
  status?: string;
  paymentUrl?: string | null;
  amount?: number | string;
  createdAt?: string;
  provider?: string | null;
  depositDetails?: Array<{
    gameProvider?: {
      name?: string;
      code?: string;
    };
    amount?: string | number;
    status?: string;
  }>;
}

const USER_ACTION_FETCH_THROTTLE_MS = 30_000;
const PAYMENT_ROUTE_MATCHERS = ['/account/finance/payment-checkout', '/account/wallet/deposit-checkout', '/account/finance/wallet/deposit'];

const normalizePendingStatus = (rawStatus: string): PendingStatus | null => {
  if (rawStatus === 'pending') return 'pending';
  return null;
};

const normalizeProviderSlug = (slug: string) => slug.toLowerCase().replace(/\s+/g, '-');

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'NA';

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatTimeAgo = (iso: string): string => {
  const value = new Date(iso).getTime();
  if (Number.isNaN(value)) return 'just now';
  const seconds = Math.max(0, Math.floor((Date.now() - value) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const THIRTY_MINUTES_MS = 30 * 60 * 1000;
const PENDING_ITEM_LIMIT = 5;

const isWalletDepositName = (name: string) => name.trim().toLowerCase() === 'deposit';

const mapHistoryToPending = (rows: HistoryTransaction[]): PendingTransaction[] => {
  const cutoff = Date.now() - THIRTY_MINUTES_MS;
  const mapped = rows.flatMap((tx) => {
    const rawStatus = String(tx.status || '').toLowerCase();
    const status = normalizePendingStatus(rawStatus);
    const paymentUrl = tx.paymentUrl || '';
    if (!status || tx.type !== 'deposit' || !paymentUrl || !tx.createdAt) return [];

    const createdAtMs = new Date(tx.createdAt).getTime();
    if (Number.isNaN(createdAtMs) || createdAtMs < cutoff) return [];

    const primaryDetail = tx.depositDetails?.[0];
    const hasGameProvider = Boolean(primaryDetail?.gameProvider?.name || primaryDetail?.gameProvider?.code);
    const providerName = primaryDetail?.gameProvider?.name || 'Deposit';
    const providerCode = primaryDetail?.gameProvider?.code || providerName;
    const amountRaw = primaryDetail?.amount ?? tx.amount ?? 0;
    const amount = typeof amountRaw === 'number' ? amountRaw : Number(amountRaw) || 0;

    return [{
      id: tx.transactionId || String(tx.id || ''),
      paymentUrl,
      transactionId: tx.transactionId,
      platformName: providerName,
      platformSlug: normalizeProviderSlug(providerCode),
      amount,
      status,
      createdAt: tx.createdAt || new Date().toISOString(),
      provider: tx.provider ?? null,
      isWalletDeposit: !hasGameProvider || isWalletDepositName(providerName),
    }];
  });

  mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return mapped.slice(0, PENDING_ITEM_LIMIT);
};

export default function PendingTransactionFloater() {
  const location = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastFetchAtRef = useRef<number>(0);
  const isFetchingRef = useRef(false);
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransaction[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [brokenIcons, setBrokenIcons] = useState<Record<string, boolean>>({});
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;

  const isOnPaymentPage = useMemo(
    () => PAYMENT_ROUTE_MATCHERS.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  const fetchPendingTransactions = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setPendingTransactions([]);
      return;
    }
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    lastFetchAtRef.current = Date.now();

    try {
      const now = new Date();
      const from = new Date(Date.now() - THIRTY_MINUTES_MS).toISOString();
      const to = now.toISOString();
      const response = await apiService.get<{ transactions?: HistoryTransaction[] }>(
        `/payment/history?page=1&limit=${PENDING_ITEM_LIMIT}&type=deposit&status=pending&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        true,
        true
      );
      const latestPending = mapHistoryToPending(response.data?.transactions || []);
      setPendingTransactions(latestPending);
      if (latestPending.length === 0 && isPanelOpenRef.current) {
        setIsPanelOpen(false);
      }
    } catch {
      // Keep previous state as-is on network failures.
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const fetchPendingTransactionsIfAllowed = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchAtRef.current < USER_ACTION_FETCH_THROTTLE_MS) {
      return;
    }
    void fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  useEffect(() => {
    void fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  useEffect(() => {
    if (!isOnPaymentPage) {
      void fetchPendingTransactions();
    }
  }, [isOnPaymentPage, fetchPendingTransactions]);

  useEffect(() => {
    const handleUserAction = () => {
      fetchPendingTransactionsIfAllowed();
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchPendingTransactionsIfAllowed();
      }
    };

    window.addEventListener('pointerdown', handleUserAction);
    window.addEventListener('keydown', handleUserAction);
    window.addEventListener('touchstart', handleUserAction);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('pointerdown', handleUserAction);
      window.removeEventListener('keydown', handleUserAction);
      window.removeEventListener('touchstart', handleUserAction);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [fetchPendingTransactionsIfAllowed]);

  useEffect(() => {
    if (!isPanelOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(event.target as Node)) return;
      setIsPanelOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isPanelOpen]);

  useEffect(() => {
    if (isOnPaymentPage && isPanelOpen) {
      setIsPanelOpen(false);
    }
  }, [isOnPaymentPage, isPanelOpen]);

  const handleResumeTransaction = (tx: PendingTransaction) => {
    playClickSound();
    setIsPanelOpen(false);

    if (shouldOpenInExternalTab(tx.provider)) {
      window.open(tx.paymentUrl, '_blank', 'noopener,noreferrer');
    }

    const resumeState = {
      resumePayment: {
        paymentUrl: tx.paymentUrl,
        transactionId: tx.transactionId,
        amount: tx.amount,
        platformName: tx.platformName,
        provider: tx.provider,
        source: 'pending_floater' as const,
      },
    };

    if (tx.isWalletDeposit) {
      navigate('/account/finance/wallet/deposit', { state: resumeState });
      return;
    }

    // Legacy game checkout resume → Deposit (wallet-only game funding).
    navigate('/account/finance/deposit', { state: resumeState });
  };

  if (pendingTransactions.length === 0 || isOnPaymentPage) {
    return null;
  }

  const pendingCount = pendingTransactions.length;

  const floaterUi = (
    <>
      <style>{`
        @keyframes pending-floater-spin { to { transform: rotate(360deg); } }
        @keyframes pending-floater-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
        @keyframes pending-floater-slide-in {
          from { opacity: 0; transform: translateX(-14px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {isPanelOpen && (
        <button
          type="button"
          aria-label="Close pending transactions panel"
          className="fixed inset-0 z-[79] bg-black/50 backdrop-blur-sm"
          onClick={() => setIsPanelOpen(false)}
        />
      )}

      <div className="fixed left-0 top-1/2 z-[80] flex items-center -translate-y-1/2 pointer-events-none">
        <div className="pointer-events-auto flex items-center">
          <button
            type="button"
            onClick={() => {
              playClickSound();
              setIsPanelOpen((open) => !open);
            }}
            className="group h-[68px] w-[44px] hover:w-[148px] border-y border-r border-border-ice rounded-r-xl overflow-hidden transition-[width] duration-200 ease-out bg-surface-strong shadow-card"
            aria-expanded={isPanelOpen}
            aria-controls="pending-transactions-panel"
          >
            <span className="flex h-full items-center gap-2 pl-3 pr-4">
              <span className="relative inline-flex">
                <Loader2
                  className="h-5 w-5 text-warning"
                  style={{ animation: 'pending-floater-spin 1.15s linear infinite' }}
                />
                <span
                  className={`absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full ${fsClass.base} font-semibold leading-[18px] text-black bg-warning`}
                  style={{ animation: 'pending-floater-pulse 1.2s ease-in-out infinite' }}
                >
                  {pendingCount}
                </span>
              </span>
              <span className={`max-w-0 opacity-0 whitespace-nowrap ${fsClass.base} text-ink/90 transition-all duration-200 ease-out group-hover:max-w-[100px] group-hover:opacity-100`}>
                {pendingCount} pending
              </span>
            </span>
          </button>

          {isPanelOpen && (
            <aside
              id="pending-transactions-panel"
              ref={panelRef}
              className="wf-pending-floater ml-2 max-h-[78vh] overflow-hidden rounded-xl border border-border-ice cosmos-panel shadow-[0_20px_45px_rgba(0,0,0,0.48)]"
              style={{
                width: 'min(22rem, calc(100vw - 3.5rem))',
                animation: 'pending-floater-slide-in 0.22s ease-out',
              }}
            >
              <header className="wf-pending-floater__header flex items-center justify-between px-4 py-3.5 border-b border-white/10">
                <div className="min-w-0">
                  <h3 className={`${typeRole.body} font-semibold text-ink m-0`}>Pending transactions</h3>
                  <p className={`${typeRole.caption} text-caption-muted m-0 mt-0.5`}>Tap to resume payment</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPanelOpen(false)}
                  className="p-1.5 rounded-md text-body-muted hover:bg-white/10 hover:text-ink transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </header>

              <div className="max-h-[calc(78vh-68px)] cosmos-scroll-y p-2">
                {pendingTransactions.map((tx) => {
                  const iconPath = `/assets/games/${tx.platformSlug}/icon.png`;
                  const showIconFallback = brokenIcons[tx.id] || tx.isWalletDeposit;
                  return (
                    <button
                      key={tx.id}
                      type="button"
                      onClick={() => handleResumeTransaction(tx)}
                      className="wf-pending-floater__item w-full rounded-lg px-3 py-3 text-left transition-colors hover:bg-white/[0.06] focus-visible:outline-none focus-visible:shadow-focus-ring"
                    >
                      <div className="grid grid-cols-[36px_minmax(0,1fr)_auto] gap-3 items-center">
                        {!showIconFallback ? (
                          <img
                            src={iconPath}
                            alt={tx.platformName}
                            className="w-9 h-9 rounded-[8px] object-cover bg-white/10 border border-white/10"
                            onError={() => setBrokenIcons((prev) => ({ ...prev, [tx.id]: true }))}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-[8px] bg-money-in/15 border border-money-in/30 flex items-center justify-center text-money-in">
                            {tx.isWalletDeposit ? (
                              <Wallet className="h-4 w-4" aria-hidden />
                            ) : (
                              <span className={`${fsClass.base} font-semibold`}>{getInitials(tx.platformName)}</span>
                            )}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className={`truncate ${typeRole.body} text-ink font-medium m-0`}>
                            {tx.isWalletDeposit ? 'Wallet deposit' : tx.platformName}
                          </p>
                          <p className={`${typeRole.caption} text-caption-muted m-0 mt-0.5`}>
                            {tx.isWalletDeposit ? 'Wallet' : 'QR'} · {formatTimeAgo(tx.createdAt)}
                          </p>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <p className={`${typeRole.body} text-ink font-semibold m-0 tabular-nums`}>{formatAmount(tx.amount)}</p>
                          <StatusPill status="pending" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(floaterUi, document.body);
}
